import type { ActivityContentObjectDetail, ActivityContentObjectList, ContentObjectView } from 'models';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { BookmarkLineIcon, Flag1LineIcon, type MenuProps, message } from 'ui';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { useReportContentObjectModalConfirm } from 'features/report';

type P = {
  item: ActivityContentObjectDetail | ActivityContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
};

export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const { t } = useTranslation();
  const { confirm: confirmReportActivity, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { canReportContentObject } = usePermissions();
  const { mutate: unbookmark } = useUnbookmarkObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <BookmarkLineIcon size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onError() {
                    message.error(t("Something went wrong. The item couldn't be unbookmarked."));
                  },
                  onSuccess() {
                    message.success(t('Successfully unbookmarked'));
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Flag1LineIcon size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportActivity({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: 'Are you sure you want to report this activity?',
          });
        },
      });
    }

    return {
      actions,
      contexts: [reportContentObjectContextHolder],
    };
  }, [view, canReportContentObject, item, reportContentObjectContextHolder, t, confirmReportActivity, unbookmark]);
};
